import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/CompanyPurge.css';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import { Company, PurgeCounts, PurgePreviewResponse, PurgeCompanyResponse } from '../types/auth';
import { useDataRefresh } from '../contexts/DataRefreshContext';
import { useToast } from '../contexts/ToastContext';
import { formatDate } from '../utils/dateUtils';

// Labels for every row count returned by the preview / purge endpoints
const COUNT_LABELS: Array<{ key: keyof PurgeCounts; label: string }> = [
  { key: 'users', label: 'Users' },
  { key: 'testSuites', label: 'Test suites' },
  { key: 'testSuiteRuleMappings', label: 'Rule mappings' },
  { key: 'testSuiteReceiptEvents', label: 'Receipt events' },
  { key: 'certifications', label: 'Certifications' },
  { key: 'companyPages', label: 'Company pages' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'documents', label: 'Documents' },
  { key: 'apiPerformanceRecords', label: 'API performance records' },
  { key: 'company', label: 'Company record' },
];

const CompanyPurge: React.FC = () => {
  const { t } = useTranslation();
  const { refreshAllData } = useDataRefresh();
  const { showSuccess, showError } = useToast();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Purge dialog state
  const [purgeTarget, setPurgeTarget] = useState<Company | null>(null);
  const [preview, setPreview] = useState<PurgePreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [armed, setArmed] = useState(false); // set by the first click of the double-click
  const [purging, setPurging] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [result, setResult] = useState<PurgeCompanyResponse | null>(null);

  const isAdmin = () => {
    const userData = authStorage.getUserData();
    return userData?.companyId === null && userData?.userType === 1;
  };

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }
      const response = await authApi.getAllCompanies(token);
      setCompanies(response);
    } catch (err) {
      console.error('Failed to load companies:', err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load companies. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const filteredCompanies = companies
    .filter(company => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return true;
      return (
        company.companyName.toLowerCase().includes(term) ||
        (company.orgNumber || '').toLowerCase().includes(term) ||
        String(company.id) === term
      );
    })
    .sort((a, b) => a.companyName.localeCompare(b.companyName));

  const resetDialog = () => {
    setPurgeTarget(null);
    setPreview(null);
    setPreviewLoading(false);
    setConfirmInput('');
    setAcknowledged(false);
    setArmed(false);
    setPurging(false);
    setDialogError('');
    setResult(null);
  };

  const handleOpenPurgeDialog = async (company: Company) => {
    resetDialog();
    setPurgeTarget(company);
    setPreviewLoading(true);

    try {
      const token = authStorage.getAccessToken();
      if (!token) {
        setDialogError('Authentication token not found. Please log in again.');
        return;
      }
      const previewData = await authApi.getCompanyPurgePreview(token, company.id);
      setPreview(previewData);
    } catch (err) {
      console.error('Failed to load purge preview:', err);
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setDialogError('This company no longer exists. The list has been refreshed.');
          loadCompanies();
        } else {
          setDialogError(err.message);
        }
      } else {
        setDialogError('Failed to load the deletion preview. Please try again.');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  // The confirmation input must echo back the company id from the preview
  const confirmationMatches =
    preview !== null && confirmInput.trim() === String(preview.companyId);

  const canPurge = !!preview && confirmationMatches && acknowledged && !purging && !result;

  // First click only arms the button - the purge itself runs on double click
  const handlePurgeSingleClick = () => {
    if (!canPurge) return;
    setArmed(true);
  };

  const handlePurgeDoubleClick = async () => {
    if (!canPurge || !preview) return;

    setPurging(true);
    setDialogError('');

    try {
      const token = authStorage.getAccessToken();
      if (!token) {
        setDialogError('Authentication token not found. Please log in again.');
        return;
      }

      const purgeResult = await authApi.purgeCompany(token, preview.companyId, preview.companyId);
      setResult(purgeResult);
      setArmed(false);
      showSuccess(
        `${purgeResult.companyName} and all related data have been permanently deleted.`
      );

      await loadCompanies();
      if (refreshAllData) {
        await refreshAllData();
      }
    } catch (err) {
      console.error('Failed to purge company:', err);
      setArmed(false);
      if (err instanceof ApiError) {
        setDialogError(err.message);
        if (err.status === 404) {
          showError('Company not found. It may already have been deleted.');
          loadCompanies();
        } else {
          showError(err.message);
        }
      } else {
        setDialogError('Failed to delete the company. Please try again.');
        showError('Failed to delete the company. Please try again.');
      }
    } finally {
      setPurging(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="company-purge">
        <div className="purge-error-message">
          {t('common.unauthorized', 'Unauthorized access. Admin privileges required.')}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="company-purge">
        <div className="loading">{t('common.loading', 'Loading companies...')}</div>
      </div>
    );
  }

  return (
    <div className="company-purge">
      <div className="company-purge-header">
        <h1>Permanent Deletion</h1>
      </div>

      <div className="danger-banner">
        <span className="danger-banner-icon" aria-hidden="true">⚠️</span>
        <div className="danger-banner-text">
          <strong>This page permanently destroys data.</strong>
          <p>
            Purging a company deletes the company and everything attached to it &mdash; its users,
            test suites (including runs still in progress), rule mappings, receipt events,
            certifications, company pages, notifications, documents, uploaded files on disk and API
            performance records. There is no undo, no archive and no soft-delete flag left behind.
          </p>
          <p>
            To deactivate a company instead, use <strong>Companies &rarr; Edit</strong> or the normal
            delete action there, which only marks the company as deleted.
          </p>
        </div>
      </div>

      {error && <div className="purge-error-message">{error}</div>}

      <div className="purge-controls-row">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search companies by name, organization number or id..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="clear-filters-btn"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <button className="refresh-companies-btn" onClick={loadCompanies} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="results-summary">
        <p>
          {searchTerm
            ? `Showing ${filteredCompanies.length} of ${companies.length} companies`
            : `Total companies: ${companies.length}`}
        </p>
      </div>

      <div className="purge-table-container">
        <table className="purge-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Company Name</th>
              <th>Organization Number</th>
              <th>Users</th>
              <th>Status</th>
              <th>Last Updated</th>
              <th className="actions-column">Danger Zone</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.map((company) => (
              <tr key={company.id} className="purge-row">
                <td className="purge-cell purge-id-cell">{company.id}</td>
                <td className="purge-cell">
                  <span className="purge-company-name">{company.companyName}</span>
                </td>
                <td className="purge-cell">{company.orgNumber}</td>
                <td className="purge-cell">
                  <span className="user-count-badge">{company.users?.length || 0} users</span>
                </td>
                <td className="purge-cell">
                  <span className={`status-badge ${company.isActive ? 'active' : 'inactive'}`}>
                    {company.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {company.isDeleted && <span className="status-badge deleted">Soft-deleted</span>}
                </td>
                <td className="purge-cell">{formatDate(company.lastUpdatedDate)}</td>
                <td className="purge-cell">
                  <button
                    className="purge-button"
                    onClick={() => handleOpenPurgeDialog(company)}
                    title={`Permanently delete ${company.companyName} and all of its data`}
                  >
                    🗑️ Delete everything
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredCompanies.length === 0 && (
        <div className="no-results">
          <p>
            {searchTerm
              ? 'No companies found matching your search criteria.'
              : 'No companies found.'}
          </p>
        </div>
      )}

      {/* Confirmation dialog */}
      {purgeTarget && (
        <div className="modal-overlay">
          <div className="modal-content purge-modal">
            <div className="modal-header purge-modal-header">
              <h2>
                {result ? 'Company permanently deleted' : 'Permanently delete company'}
              </h2>
            </div>

            <div className="modal-body">
              {previewLoading && (
                <div className="loading">Loading what would be deleted...</div>
              )}

              {!previewLoading && dialogError && !result && (
                <div className="purge-error-message">{dialogError}</div>
              )}

              {/* Step 1 + 2: preview and confirmation */}
              {!previewLoading && preview && !result && (
                <>
                  <div className="purge-target">
                    <span className="purge-target-name">{preview.companyName}</span>
                    <span className="purge-target-id">ID {preview.companyId}</span>
                  </div>

                  <div className="purge-warning critical">
                    <strong>⚠️ This cannot be undone.</strong>
                    <p>
                      Everything listed below is deleted permanently, including files stored on
                      disk. There is no backup taken by this action and no way to restore the data
                      from the application.
                    </p>
                  </div>

                  {preview.activeTestSuites > 0 && (
                    <div className="purge-warning">
                      <strong>
                        {preview.activeTestSuites} test suite
                        {preview.activeTestSuites === 1 ? ' is' : 's are'} still in progress.
                      </strong>
                      <p>
                        {preview.activeTestSuites === 1 ? 'It' : 'They'} will be deleted together
                        with the company. Any ongoing certification work is lost.
                      </p>
                    </div>
                  )}

                  {preview.internalUsers > 0 && (
                    <div className="purge-warning critical">
                      <strong>
                        {preview.internalUsers} internal/admin account
                        {preview.internalUsers === 1 ? '' : 's'} belong to this company.
                      </strong>
                      <p>
                        This is unusual. {preview.internalUsers === 1 ? 'That account' : 'Those accounts'}{' '}
                        will be deleted too and can no longer sign in. Check with another
                        administrator before continuing.
                      </p>
                    </div>
                  )}

                  {preview.isDeleted && (
                    <div className="purge-note">
                      This company is already soft-deleted. Purging removes its rows from the
                      database entirely.
                    </div>
                  )}

                  <h3 className="purge-section-title">What will be deleted</h3>
                  <div className="purge-counts-grid">
                    {COUNT_LABELS.map(({ key, label }) => (
                      <div key={key} className="purge-count-item">
                        <span className="purge-count-value">{preview.counts[key]}</span>
                        <span className="purge-count-label">{label}</span>
                      </div>
                    ))}
                    <div className="purge-count-item">
                      <span className="purge-count-value">{preview.filesOnDisk}</span>
                      <span className="purge-count-label">Files on disk</span>
                    </div>
                  </div>

                  <div className="purge-confirm-block">
                    <label className="purge-confirm-label" htmlFor="purge-confirm-input">
                      Type the company id <code>{preview.companyId}</code> to confirm you are
                      deleting <strong>{preview.companyName}</strong>:
                    </label>
                    <input
                      id="purge-confirm-input"
                      type="text"
                      className={`purge-confirm-input ${
                        confirmInput && !confirmationMatches ? 'mismatch' : ''
                      }`}
                      value={confirmInput}
                      onChange={(e) => {
                        setConfirmInput(e.target.value);
                        setArmed(false);
                      }}
                      placeholder={String(preview.companyId)}
                      autoComplete="off"
                      disabled={purging}
                    />
                    {confirmInput && !confirmationMatches && (
                      <span className="purge-confirm-hint mismatch">
                        That does not match company id {preview.companyId}.
                      </span>
                    )}

                    <label className="purge-ack">
                      <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => {
                          setAcknowledged(e.target.checked);
                          setArmed(false);
                        }}
                        disabled={purging}
                      />
                      <span>
                        I understand that this permanently deletes {preview.companyName}, its{' '}
                        {preview.counts.users} user{preview.counts.users === 1 ? '' : 's'} and all
                        related data, and that it cannot be undone.
                      </span>
                    </label>
                  </div>
                </>
              )}

              {/* Step 3: result */}
              {result && (
                <>
                  <div className="purge-success">
                    <strong>{result.message}</strong>
                  </div>

                  <h3 className="purge-section-title">Rows deleted</h3>
                  <div className="purge-counts-grid">
                    {COUNT_LABELS.map(({ key, label }) => (
                      <div key={key} className="purge-count-item">
                        <span className="purge-count-value">{result.deleted[key]}</span>
                        <span className="purge-count-label">{label}</span>
                      </div>
                    ))}
                    <div className="purge-count-item">
                      <span className="purge-count-value">{result.filesDeleted}</span>
                      <span className="purge-count-label">Files deleted</span>
                    </div>
                  </div>

                  {result.fileDeletionFailures.length > 0 && (
                    <div className="purge-warning">
                      <strong>
                        {result.fileDeletionFailures.length} file
                        {result.fileDeletionFailures.length === 1 ? '' : 's'} could not be removed
                        from disk.
                      </strong>
                      <p>
                        The database rows were deleted successfully. Ask ops to clean up these
                        leftovers:
                      </p>
                      <ul className="purge-failed-files">
                        {result.fileDeletionFailures.map((file) => (
                          <li key={file}>{file}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer purge-modal-footer">
              {result ? (
                <button className="btn-primary" onClick={resetDialog}>
                  Close
                </button>
              ) : (
                <>
                  <button className="btn-secondary" onClick={resetDialog} disabled={purging}>
                    Cancel
                  </button>
                  <div className="purge-final-action">
                    {armed && !purging && (
                      <span className="purge-armed-hint">
                        Double-click to delete permanently
                      </span>
                    )}
                    <button
                      className={`purge-confirm-button ${armed ? 'armed' : ''}`}
                      onClick={handlePurgeSingleClick}
                      onDoubleClick={handlePurgeDoubleClick}
                      disabled={!canPurge}
                      title={
                        canPurge
                          ? 'Double-click to permanently delete this company'
                          : 'Type the company id and tick the acknowledgement first'
                      }
                    >
                      {purging ? 'Deleting...' : 'Double-click to PURGE'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPurge;
